import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  Edit3,
  Save,
  RefreshCw,
  Check,
  Info,
  Bold,
  Italic,
  ChevronLeft
} from 'lucide-react';
import Button from '../common/Button';
import Modal from '../common/Modal';
import { API_CONFIG, buildApiUrl } from '../../firebase/config';

// Default registration template - moved outside component to prevent re-renders
const defaultTemplate = {
    id: 'registration_welcome_steps',
    name: 'Welcome to YAU – Important Next Steps',
    subject: 'Welcome to YAU – Important Next Steps',
    type: 'registration',
    variables: ['{{parentFirst}}', '{{email}}', '{{studentNames}}', '{{studentSport}}', '{{registrationDate}}'],
    htmlContent: `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Welcome to YAU – Important Next Steps</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { color: #2c3e50; margin-bottom: 20px; }
        .contact-box { background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
        .info-box { background-color: #e9ecef; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
        .contact-hours { margin: 10px 0; padding-left: 20px; }
        .signature { margin-top: 30px; font-style: italic; }
    </style>
</head>
<body>
    <div class="header">
        <h2>Welcome to YAU – Important Next Steps</h2>
    </div>
    
    <p>Hi {{parentFirst}},</p>
    
    <p>Thank you for signing up for YAU Evening Activities.</p>
    
    <p style="margin-bottom: 25px;"><strong>To receive important updates regarding practice times and locations, please contact us immediately during the following hours:</strong></p>
    
    <div class="contact-box">
        <p style="margin: 0; font-weight: bold;">Contact Hours:</p>
        <ul class="contact-hours">
            <li>Monday – Friday: 10:00 AM – 3:00 PM (EST)</li>
            <li>Saturday: 8:00 AM – 12:00 PM (EST)</li>
        </ul>
    </div>
    
    <div class="info-box">
        <p style="margin: 0; font-weight: bold;">Contact Information:</p>
        <p style="margin: 5px 0;"><strong>Phone:</strong> 301-292-3688</p>
        <p style="margin: 5px 0;"><strong>Email:</strong> FUN@YAUSports.org</p>
    </div>
    
    <p style="margin-top: 25px;">We look forward to working with your child.</p>
    
    <div class="signature">
        <p>Thank you,<br>YAU Team</p>
    </div>
</body>
</html>`,
    textContent: `Welcome to YAU – Important Next Steps

Hi {{parentFirst}},

Thank you for signing up for YAU Evening Activities.

To receive important updates regarding practice times and locations, please contact us immediately during the following hours:

• Monday – Friday: 10:00 AM – 3:00 PM (EST)
• Saturday: 8:00 AM – 12:00 PM (EST)

Phone: 301-292-3688
Email: FUN@YAUSports.org

We look forward to working with your child.

Thank you,
YAU Team`
};

const EmailTemplates = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      
      // Try to load from Firestore directly (where email sending system looks)
      try {
        const { db } = await import('../../firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');
        
        const templateDoc = await getDoc(doc(db, 'emailTemplates', 'registration_welcome_steps'));
        
        if (templateDoc.exists()) {
          const templateData = templateDoc.data();
          setTemplate({
            id: 'registration_welcome_steps',
            ...templateData,
            updatedAt: templateData.updatedAt?.toDate() || new Date()
          });
          console.log('✅ Loaded template from Firestore');
          return;
        } else {
          console.log('⚠️ No template found in Firestore, using default');
        }
      } catch (firestoreError) {
        console.warn('Firestore not available, trying API:', firestoreError.message);
        
        // Try API as fallback
        try {
          const response = await fetch(buildApiUrl(API_CONFIG.endpoints.emailTemplates.getAll));
          
          if (response.ok) {
            const data = await response.json();
            const loadedTemplates = data.data || [];
            
            // Find the registration template
            const registrationTemplate = loadedTemplates.find(t => t.id === 'registration_welcome_steps');
            if (registrationTemplate) {
              setTemplate(registrationTemplate);
              return;
            }
          }
        } catch (apiError) {
          console.warn('API not available, using fallback:', apiError.message);
        }
      }
      
      // Final fallback: Use default template
      console.log('Using default template');
      setTemplate(defaultTemplate);
      
    } catch (error) {
      console.error('Error loading template:', error);
      setTemplate(defaultTemplate);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleSaveTemplate = async (updates) => {
    try {
      setSaving(true);
      
      // Save directly to Firestore where email sending system looks for templates
      try {
        // Import Firebase admin SDK directly for Firestore access
        const { db } = await import('../../firebase/config');
        const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
        
        // Prepare template data with isActive=true so email sending system finds it
        const templateData = {
          ...template,
          ...updates,
          isActive: true, // This is critical - email system looks for isActive: true
          updatedAt: serverTimestamp(),
          updatedBy: 'admin'
        };
        
        // Save to Firestore emailTemplates collection
        await setDoc(doc(db, 'emailTemplates', 'registration_welcome_steps'), templateData);
        
        // Update local state
        setTemplate(prev => ({ ...prev, ...updates, updatedAt: new Date() }));
        setEditingTemplate(null);
        alert('✅ Template updated successfully! Email sending system will now use this template.');
        return;
        
      } catch (firestoreError) {
        console.warn('Firestore not available, trying API:', firestoreError.message);
        
        // Fallback to API
        const response = await fetch(buildApiUrl(API_CONFIG.endpoints.emailTemplates.update, { templateId: 'registration_welcome_steps' }), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...updates, isActive: true }), // Ensure isActive is true
        });

        if (response.ok) {
          setTemplate(prev => ({ ...prev, ...updates, updatedAt: new Date() }));
          setEditingTemplate(null);
          alert('✅ Template updated successfully!');
          return;
        }
      }
      
      // Final fallback: Update local state only
      setTemplate(prev => ({ ...prev, ...updates, updatedAt: new Date() }));
      setEditingTemplate(null);
      alert('⚠️ Template updated locally only. Email sending system may not see changes until Firestore is available.');
      
    } catch (error) {
      console.error('Error saving template:', error);
      alert('❌ Error saving template: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (!window.confirm('Are you sure you want to reset this template to its default content? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      
      if (defaultTemplate) {
        setEditingTemplate({
          ...template,
          subject: defaultTemplate.subject,
          textContent: defaultTemplate.textContent,
          htmlContent: defaultTemplate.htmlContent
        });
        alert('✅ Template reset to default! Click "Save Changes" to apply.');
      }
    } catch (error) {
      console.error('Error resetting template:', error);
      alert('❌ Error resetting template: ' + error.message);
    } finally {
      setSaving(false);
    }
  };








  // Available variables from Registration form data (only those with actual data)
  const availableVariables = [
    { label: 'Parent First Name', value: '{{parentFirst}}' },
    { label: 'Parent Last Name', value: '{{parentLast}}' },
    { label: 'Parent Email', value: '{{email}}' },
    { label: 'Parent Phone', value: '{{mobile}}' },
    { label: 'Student Names', value: '{{studentNames}}' },
    { label: 'Student Sport', value: '{{studentSport}}' },
    { label: 'Student Location', value: '{{studentLocation}}' },
    { label: 'Registration Date', value: '{{registrationDate}}' },
    { label: 'Payment Amount', value: '{{paymentAmount}}' },
    { label: 'Payment Plan', value: '{{paymentPlan}}' }
  ];

  // Simplified Template Editor Component
  const TemplateEditor = ({ template, onSave, onCancel, onReset, saving }) => {
    const [formData, setFormData] = useState({
      subject: template.subject,
      textContent: template.textContent,
      htmlContent: template.htmlContent,
      variables: template.variables || ['{{parentFirst}}', '{{email}}', '{{studentNames}}', '{{studentSport}}', '{{registrationDate}}']
    });

    const [formatState, setFormatState] = useState({
      bold: false,
      italic: false
    });

    const [showVariableDropdown, setShowVariableDropdown] = useState(false);

  const handleInputChange = (field, value) => {
    const updates = { [field]: value };
    
    // If text content is changing, also update HTML content
    if (field === 'textContent') {
      // Convert markdown-like syntax to HTML
      let htmlContent = value
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // **bold** to <strong>
        .replace(/\*(.*?)\*/g, '<em>$1</em>') // *italic* to <em>
        .replace(/\n/g, '<br>'); // newlines to <br>
      
      // Wrap in basic HTML structure
      htmlContent = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Welcome to YAU – Important Next Steps</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { color: #2c3e50; margin-bottom: 20px; }
        .contact-box { background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
        .info-box { background-color: #e9ecef; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
        .contact-hours { margin: 10px 0; padding-left: 20px; }
        .signature { margin-top: 30px; font-style: italic; }
    </style>
</head>
<body>
    <div class="header">
        <h2>${formData.subject || 'Welcome to YAU – Important Next Steps'}</h2>
    </div>
    
    <div class="content">
        ${htmlContent}
    </div>
    
    <div class="signature">
        <p>Thank you,<br>YAU Team</p>
    </div>
</body>
</html>`;
      
      updates.htmlContent = htmlContent;
    }
    
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  const handleVariableToggle = (variable) => {
    const currentVariables = formData.variables || [];
    const variableExists = currentVariables.includes(variable.value);
    
    if (variableExists) {
      // Remove variable
      setFormData(prev => ({
        ...prev,
        variables: currentVariables.filter(v => v !== variable.value)
      }));
    } else {
      // Add variable
      setFormData(prev => ({
        ...prev,
        variables: [...currentVariables, variable.value]
      }));
    }
  };

  const insertVariableAtCursor = (variable) => {
    const textarea = document.getElementById('emailContent');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentContent = formData.textContent;
    
    const newContent = currentContent.substring(0, start) + variable.value + currentContent.substring(end);
    handleInputChange('textContent', newContent);
    
    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.value.length, start + variable.value.length);
    }, 0);
  };

  const handleTextFormatting = (format) => {
    const textarea = document.getElementById('emailContent');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.textContent.substring(start, end);
    
    // Toggle the format state
    setFormatState(prev => ({
      ...prev,
      [format]: !prev[format]
    }));
    
    // Apply formatting to selected text
    let formattedText = selectedText;
    if (format === 'bold') {
      formattedText = formatState.bold ? 
        selectedText.replace(/\*\*(.*?)\*\*/g, '$1') : // Remove bold
        `**${selectedText}**`; // Add bold
    } else if (format === 'italic') {
      formattedText = formatState.italic ? 
        selectedText.replace(/\*(.*?)\*/g, '$1') : // Remove italic
        `*${selectedText}*`; // Add italic
    }
    
    const newContent = formData.textContent.substring(0, start) + formattedText + formData.textContent.substring(end);
    handleInputChange('textContent', newContent);
  };

  const handleSave = () => {
    onSave(formData);
  };

  // Sample data for preview (matches Registration form data structure)
  const sampleData = {
    parentFirst: "John",
    parentLast: "Doe",
    email: "john.doe@example.com",
    mobile: "+12015551234",
    countryCode: "+1",
    studentFirstNames: "Jane, Johnny",
    studentLastNames: "Doe, Doe",
    studentNames: "Jane Doe, Johnny Doe",
    studentDob: "01-15-2018, 03-20-2020",
    studentGrade: "4th, 2nd",
    studentSchool: "Lincoln Elementary",
    studentSport: "Soccer, Basketball",
    studentLocation: "Central Park",
    uniformTop: "Youth M",
    uniformBottom: "Youth L",
    includeUniform: true,
    registrationDate: new Date().toLocaleDateString(),
    paymentAmount: "$200.00",
    paymentPlan: "oneTime"
  };

  // Replace variables and parse markdown in content for preview
  const getPreviewContent = (content) => {
    let previewContent = content;
    
    // Replace variables first
    Object.keys(sampleData).forEach(key => {
      const variable = `{{${key}}}`;
      const value = sampleData[key];
      previewContent = previewContent.replace(new RegExp(variable, 'g'), value);
    });
    
    // Parse simple markdown
    previewContent = previewContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'); // Bold
    previewContent = previewContent.replace(/\*(.*?)\*/g, '<em>$1</em>'); // Italic
    
    return previewContent;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Form Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Registration Thank-You Email</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Subject
          </label>
          <input
            type="text"
            value={formData.subject}
            onChange={(e) => handleInputChange('subject', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter email subject"
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Available Variables
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowVariableDropdown(!showVariableDropdown)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-left flex justify-between items-center"
              disabled={saving}
            >
              <span className="text-sm text-gray-700">
                {formData.variables.length > 0 
                  ? `${formData.variables.length} variables selected` 
                  : 'Select variables from registration data'
                }
              </span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showVariableDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                <div className="p-2">
                  <div className="mb-2 pb-2 border-b border-gray-200">
                    <p className="text-xs text-gray-500 font-medium">Click to insert at cursor, or select to track:</p>
                  </div>
                  {availableVariables.map((variable) => (
                    <div key={variable.value} className="flex items-center p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={formData.variables.includes(variable.value)}
                        onChange={() => handleVariableToggle(variable)}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        disabled={saving}
                      />
                      <button
                        type="button"
                        onClick={() => insertVariableAtCursor(variable)}
                        className="flex-1 text-left group"
                        disabled={saving}
                      >
                        <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                          {variable.label}
                        </div>
                        <div className="text-xs text-gray-500">{variable.value}</div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Show selected variables as tags */}
          {formData.variables.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.variables.map((variable) => (
                <span key={variable} className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {variable}
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Content
          </label>
          <div className="mb-2">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleTextFormatting('bold')}
                className={`px-3 py-1 text-sm border rounded flex items-center gap-1 transition-colors ${
                  formatState.bold 
                    ? 'bg-blue-500 text-white border-blue-500' 
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                disabled={saving}
                title="Bold (Ctrl+B)"
              >
                <Bold size={14} />
                {formatState.bold ? 'Bold On' : 'Bold'}
              </button>
              <button
                type="button"
                onClick={() => handleTextFormatting('italic')}
                className={`px-3 py-1 text-sm border rounded flex items-center gap-1 transition-colors ${
                  formatState.italic 
                    ? 'bg-blue-500 text-white border-blue-500' 
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                disabled={saving}
                title="Italic (Ctrl+I)"
              >
                <Italic size={14} />
                {formatState.italic ? 'Italic On' : 'Italic'}
              </button>
              <span className="text-xs text-gray-500 ml-2 flex items-center">
                Click to toggle formatting on/off. Select text to format.
              </span>
            </div>
          </div>
          <textarea
            id="emailContent"
            value={formData.textContent}
            onChange={(e) => handleInputChange('textContent', e.target.value)}
            rows={12}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter email content in plain text. Use **bold** for bold text and *italic* for italic text."
            disabled={saving}
          />
        </div>

        <div className="flex justify-start items-center pt-4 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={onReset}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Reset to Default
          </Button>
        </div>
      </div>

      {/* Preview Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={saving}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2"
              size="sm"
            >
              <Save size={14} />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="space-y-4">
            <div className="border-b border-gray-200 pb-4">
              <h4 className="font-semibold text-gray-900">{formData.subject || 'Email Subject'}</h4>
              <p className="text-sm text-gray-500">To: {sampleData.memberEmail}</p>
            </div>
            
            <div className="min-h-[400px]">
              <div 
                className="whitespace-pre-wrap text-sm text-gray-800 font-sans"
                dangerouslySetInnerHTML={{ 
                  __html: getPreviewContent(formData.textContent) || 'Email content will appear here as you type...' 
                }}
              />
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-500">
                Selected variables: {formData.variables.join(', ') || 'None selected'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Click variables above to insert them at cursor position
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between bg-white p-4 rounded-lg ">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="text-blue-600" size={28} />
              Registration Thank-You Email Template
            </h1>
            <p className="text-gray-600 mt-2">
              Manage the automated email sent to members after registration and payment
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => window.history.back()}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <ChevronLeft size={16} />
              Back
            </Button>

            <Button
              onClick={() => setEditingTemplate(template)}
              className="flex items-center gap-2"
            >
              <Edit3 size={16} />
              Edit Template
            </Button>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex items-start">
          <Info className="text-blue-600 mt-0.5 mr-3" size={20} />
          <div>
            <h3 className="text-sm font-semibold text-blue-800">Email Template Management</h3>
            <p className="text-sm text-blue-700 mt-1">
              Changes to this email template will apply immediately to all new member registrations and payments. 
              Use variables like {'{{memberName}}'} to personalize emails.
            </p>
          </div>
        </div>
      </div>

      {/* Template Preview */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Current Template</h2>
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                  <Check size={12} />
                  Active
                </span>
              </div>
              
              <p className="text-gray-600 mb-3">{template.subject}</p>
              
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>Type: {template.type}</span>
                <span>Last updated: {template.updatedAt?.toDate?.()?.toLocaleDateString() || 'Never'}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditingTemplate(template)}
                className="flex items-center gap-1"
              >
                <Edit3 size={14} />
                Edit
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Template Modal */}
      {editingTemplate && (
        <Modal
          isOpen={true}
          onClose={() => setEditingTemplate(null)}
          title="Edit Registration Thank-You Email"
          size="large"
        >
          <TemplateEditor
            template={editingTemplate}
            onSave={handleSaveTemplate}
            onCancel={() => setEditingTemplate(null)}
            onReset={handleResetToDefault}
            saving={saving}
          />
        </Modal>
      )}
    </div>
  );
};

export default EmailTemplates;
